import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { useAuth, api } from "../App";
import { toast } from "sonner";
import { ArrowLeft, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { SectionContent } from "../components/SectionContent";
import { SectionQuiz } from "../components/SectionQuiz";

function shuffleOptions(questions) {
  return questions.map(q => {
    const opts = [q.correct, ...q.wrong];
    for (let i = opts.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [opts[i], opts[j]] = [opts[j], opts[i]];
    }
    return opts;
  });
}

export default function Section() {
  const { sectionId } = useParams();
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startTime] = useState(Date.now());
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizResults, setQuizResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [analogy, setAnalogy] = useState(null);
  const [loadingAnalogy, setLoadingAnalogy] = useState(false);
  const [foundEasterEgg, setFoundEasterEgg] = useState(false);
  const contentRef = useRef(null);
  const [showQuizResult, setShowQuizResult] = useState(false);
  const [showMascotReaction, setShowMascotReaction] = useState(false);
  const [showXpReward, setShowXpReward] = useState(false);
  const [showBadgeUnlock, setShowBadgeUnlock] = useState(false);
  const [showAdvanceBtn, setShowAdvanceBtn] = useState(false);
  const [mascotMood, setMascotMood] = useState("neutral");
  const [shuffledOptions, setShuffledOptions] = useState([]);
  const [showAnswers, setShowAnswers] = useState(false);
  const [xpAlreadyEarned, setXpAlreadyEarned] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    api.get(`/course/sections/${sectionId}`).then(res => {
      setSection(res.data);
      if (res.data.locked) { setSection(res.data); }
      else {
        if (res.data.quiz_questions) setShuffledOptions(shuffleOptions(res.data.quiz_questions));
      }
    }).catch(() => navigate("/course")).finally(() => setLoading(false));
  }, [sectionId, navigate]);

  const getTimeSpent = () => Math.floor((Date.now() - startTime) / 1000);

  const handleQuizAnswer = (qi, answer) => {
    if (quizSubmitted) return;
    setQuizAnswers(prev => ({ ...prev, [qi]: answer }));
  };

  const runCompletionSequence = useCallback(async (results, newBadges) => {
    setShowQuizResult(true);
    await new Promise(r => setTimeout(r, 500));
    setMascotMood(results.passed ? "happy" : "sad");
    setShowMascotReaction(true);
    await new Promise(r => setTimeout(r, 500));
    if (results.xp_earned > 0) setShowXpReward(true);
    await new Promise(r => setTimeout(r, 500));
    if (newBadges?.length > 0) setShowBadgeUnlock(true);
    await new Promise(r => setTimeout(r, 500));
    setShowAdvanceBtn(true);
  }, []);

  const submitQuiz = async () => {
    const timeSpent = getTimeSpent();
    if (Object.keys(quizAnswers).length < 3) { toast.error("Please answer all questions"); return; }

    setSubmitting(true);
    try {
      const res = await api.post("/quiz/submit", {
        section_id: parseInt(sectionId),
        answers: Object.entries(quizAnswers).map(([idx, ans]) => ({ question_index: parseInt(idx), answer: ans })),
        total_time_spent: timeSpent,
        is_retry: retryCount > 0
      });
      const results = res.data;
      if (xpAlreadyEarned && results.xp_earned > 0) results.xp_earned = 0;
      setQuizResults(results);
      setQuizSubmitted(true);

      let newBadges = [];
      if (results.passed) {
        if (!xpAlreadyEarned) setXpAlreadyEarned(true);
        try {
          const completeRes = await api.post(`/course/sections/${sectionId}/complete`, null, { params: { time_spent: Math.max(timeSpent, 240) } });
          newBadges = completeRes.data.new_badges || [];
          updateUser({ sections_completed: [...(user.sections_completed || []), parseInt(sectionId)], current_section: parseInt(sectionId) + 1 });
        } catch (err) { console.error("Section complete error:", err); }
      }
      runCompletionSequence(results, newBadges);
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to submit quiz"); }
    finally { setSubmitting(false); }
  };

  const generateAnalogy = async (refresh) => {
    if (refresh) setAnalogy(null);
    setLoadingAnalogy(true);
    try {
      const res = await api.post("/ai/analogy", { concept: section.title, section_id: parseInt(sectionId) });
      setAnalogy(res.data.analogy);
    } catch { toast.error("Failed to generate analogy"); }
    finally { setLoadingAnalogy(false); }
  };

  const collectEasterEgg = async () => {
    if (foundEasterEgg) return;
    try {
      const res = await api.post(`/course/sections/${sectionId}/easter-egg`);
      if (res.data.xp_earned > 0) { toast.success(`Hidden fragment discovered! +${res.data.xp_earned} XP`); setFoundEasterEgg(true); }
    } catch {}
  };

  const resetQuiz = () => {
    setQuizSubmitted(false); setQuizAnswers({}); setQuizResults(null);
    setShowQuizResult(false); setShowMascotReaction(false); setShowXpReward(false);
    setShowBadgeUnlock(false); setShowAdvanceBtn(false); setMascotMood("neutral");
    setShowAnswers(false); setRetryCount(prev => prev + 1);
    if (section?.quiz_questions) setShuffledOptions(shuffleOptions(section.quiz_questions));
  };

  const handleBackToLesson = () => { setShowQuiz(false); resetQuiz(); };

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin" /></div>;
  if (!section) return null;

  if (section.locked) return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 rounded-full bg-[#F7931A]/10 flex items-center justify-center mx-auto mb-6">
          <Lock className="text-[#F7931A]" size={28} />
        </div>
        <h2 className="font-heading text-2xl font-bold text-white mb-2">Section {sectionId} is Locked</h2>
        <p className="text-zinc-400 mb-2">{section.title}</p>
        <p className="text-zinc-500 text-sm mb-8">
          {section.lock_reason === "free_tier"
            ? "Upgrade your plan to unlock all 21 sections, quizzes, and the full Bitcoin Brain experience."
            : section.unlock_requirement || "Complete the previous section to continue."}
        </p>
        <div className="flex gap-3 justify-center">
          <Button onClick={() => navigate("/dashboard")} className="bg-[#161616] border border-white/10 text-white hover:bg-[#222]" data-testid="locked-back-btn">
            Back to Dashboard
          </Button>
          {section.lock_reason === "free_tier" ? (
            <Button onClick={() => navigate("/pricing")} className="btn-primary" data-testid="locked-upgrade-btn">
              View Pricing
            </Button>
          ) : (
            <Button onClick={() => navigate("/course")} className="btn-primary" data-testid="locked-course-btn">
              Back to Course
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  // Check if quiz already completed for this section
  const quizCompleted = user?.quiz_scores?.[`section_${sectionId}`] !== undefined;

  return (
    <div className="min-h-screen bg-[#050505]">
      <header className="sticky top-0 z-50 glass-darker border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate("/course")} className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors" data-testid="section-back-btn">
              <ArrowLeft size={20} /><span className="hidden sm:inline">Back to Course</span>
            </button>
            <span className="text-zinc-500 text-sm font-mono">Section {sectionId}/21</span>
          </div>
        </div>
      </header>

      <main ref={contentRef} className="max-w-4xl mx-auto px-4 py-8 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 64px)' }}>
        <AnimatePresence mode="wait">
          {!showQuiz ? (
            <SectionContent
              section={section} analogy={analogy} loadingAnalogy={loadingAnalogy}
              generateAnalogy={generateAnalogy} foundEasterEgg={foundEasterEgg}
              collectEasterEgg={collectEasterEgg} onTakeQuiz={() => setShowQuiz(true)}
              quizCompleted={quizCompleted}
            />
          ) : (
            <SectionQuiz
              section={section} sectionId={sectionId} quizAnswers={quizAnswers}
              handleQuizAnswer={handleQuizAnswer} shuffledOptions={shuffledOptions}
              quizSubmitted={quizSubmitted} quizResults={quizResults} submitting={submitting}
              submitQuiz={submitQuiz} resetQuiz={resetQuiz}
              showQuizResult={showQuizResult} showMascotReaction={showMascotReaction}
              showXpReward={showXpReward} showBadgeUnlock={showBadgeUnlock}
              showAdvanceBtn={showAdvanceBtn} mascotMood={mascotMood}
              showAnswers={showAnswers} setShowAnswers={setShowAnswers}
              retryCount={retryCount} xpAlreadyEarned={xpAlreadyEarned}
              onBackToLesson={handleBackToLesson} navigate={navigate}
            />
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
